import { FormRow } from 'framework/ui/layout';
import { useCallback } from 'react';
import { Option, LoadDropdown, DateInput, useInputHelper } from 'framework/form';
import {
  useWasteService,
  WASTE_REPORTING_METHOD,
  TicketIdsInput,
  RedmineTicketsMultiSelect,
} from 'domain/shared/waste';
import { useDateService } from 'framework/utils';
import { EmployeeInput, EMPLOYEE_TYPES_ALIAS } from 'domain/shared/employee';

type Period = {
  startDate?: string;
  endDate?: string;
};

type RedmineTicket = {
  id: string;
  projectIdentifier?: string;
  subject: string;
  violatesBusinessRules: boolean;
};

type Project = {
  id: string;
  name: string;
  projectIdentifier?: string;
};

interface LaborCostWasteRowProps {
  source: string;
  isProjectVisible?: boolean;
  redmineIdentifier?: string;
}

const LaborCostWasteRow: React.FC<LaborCostWasteRowProps> = ({ source, isProjectVisible, redmineIdentifier }) => {
  const { getValue, getRawOnChangeCallback } = useInputHelper({ source });
  const parentOnChange = getRawOnChangeCallback() || (() => {});
  const value = getValue();
  const employeeAlias: string = value?.employeeAlias;
  const period: Period = value?.period;

  const { getRedmineTickets, getProjects } = useWasteService();
  const { getToday } = useDateService();

  const getRedmineTicketsCallback = useCallback(async (): Promise<{ result: RedmineTicket[] }> => {
    return employeeAlias
      ? getRedmineTickets({ id: employeeAlias, projectIdentifier: redmineIdentifier })
      : Promise.resolve({ result: [] });
  }, [employeeAlias, redmineIdentifier, getRedmineTickets]);

  const getProjectCallback = useCallback(async (): Promise<{ result: Project[] }> => {
    return employeeAlias && period?.startDate && period?.endDate
      ? getProjects({ id: employeeAlias, period })
      : { result: [] };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employeeAlias, getProjects, period?.startDate, period?.endDate]);

  return (
    <>
      <FormRow>
        <TicketIdsInput
          isVisible={data => data?.[source]?.reportingMethod === WASTE_REPORTING_METHOD.BY_TICKET_IDS}
          tooltip="Input ticket ID(s) separated by comma."
          label="Redmine ticket ID (s)"
          source={`${source}.ticketsIds`}
        />
        <EmployeeInput
          isVisible={data =>
            data?.[source]?.reportingMethod && data?.[source]?.reportingMethod !== WASTE_REPORTING_METHOD.BY_TICKET_IDS
          }
          isInitialNameLoad={!!employeeAlias}
          source={`${source}.employeeAlias`}
          label="Worker"
          defaultFilter={{
            employeeType: {
              in: [EMPLOYEE_TYPES_ALIAS.CONTRACTOR, EMPLOYEE_TYPES_ALIAS.EMPLOYEE].filter(item => !!item),
            },
          }}
          onChangeCallback={values => {
            parentOnChange({
              ...values,
              [`${source}.ticketsIds`]: null,
            });
          }}
        />
        <RedmineTicketsMultiSelect
          source={`${source}.ticketsIds`}
          label="Redmine ticket ID (s)"
          loadMethod={getRedmineTicketsCallback}
          deps={[employeeAlias, redmineIdentifier]}
          isClearValueByUpdateDeps={false}
          isVisible={data => data?.[source]?.reportingMethod === WASTE_REPORTING_METHOD.BY_EMPLOYEE_AND_TICKET_IDS}
        />
        <DateInput
          maxDate={getToday()}
          isRequired
          isVisible={data => data?.[source]?.reportingMethod === WASTE_REPORTING_METHOD.BY_PROJECT_PERIOD}
          label="Period from"
          source={`${source}.period.startDate`}
        />
        <DateInput
          maxDate={getToday()}
          isRequired
          isVisible={data => data?.[source]?.reportingMethod === WASTE_REPORTING_METHOD.BY_PROJECT_PERIOD}
          label="Period to"
          source={`${source}.period.endDate`}
        />
      </FormRow>

      <FormRow>
        <LoadDropdown
          isVisible={data =>
            isProjectVisible ?? data?.[source]?.reportingMethod === WASTE_REPORTING_METHOD.BY_PROJECT_PERIOD
          }
          placeholder="Choose project"
          loadDataMethod={getProjectCallback}
          mapResults={(data: Project): Option => ({ value: data.id, label: data.name })}
          deps={[employeeAlias, period?.startDate, period?.endDate]}
          source={`${source}.projectId`}
          isClearValueByUpdateDeps
        />
      </FormRow>
    </>
  );
};

export default LaborCostWasteRow;
